import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { OperationType, OperationTypeSearchParams } from '../../services/operationTypeApi';

export interface OperationTypeState {
  // Data
  items: OperationType[];
  selectedItem: OperationType | null;
  
  // UI State
  loading: boolean;
  error: string | null;
  
  // Form State
  formData: {
    Name: string;
    Description: string;
    Comments: string;
    Active: boolean;
  };
  
  // Validation
  validationErrors: string[];
  
  // Search and Filter
  searchParams: OperationTypeSearchParams;
  
  // Pagination
  pagination: {
    page: number;
    pageSize: number;
    total: number;
  };
  
  // Statistics
  statistics: {
    total: number;
    active: number;
    inactive: number;
  };
  
  // Bulk Operations
  selectedIds: number[];
  bulkOperationLoading: boolean;
  
  // Success Messages
  successMessage: string;
}

const initialState: OperationTypeState = {
  // Data
  items: [],
  selectedItem: null,
  
  // UI State
  loading: false,
  error: null,
  
  // Form State
  formData: {
    Name: '',
    Description: '',
    Comments: '',
    Active: true,
  },
  
  // Validation
  validationErrors: [],
  
  // Search and Filter
  searchParams: {
    page: 1,
    pageSize: 10,
    search: '',
    active: undefined,
    sortBy: 'Name',
    sortOrder: 'asc',
  },
  
  // Pagination
  pagination: {
    page: 1,
    pageSize: 10,
    total: 0,
  },
  
  // Statistics
  statistics: {
    total: 0,
    active: 0,
    inactive: 0,
  },
  
  // Bulk Operations
  selectedIds: [],
  bulkOperationLoading: false,
  
  // Success Messages
  successMessage: '',
};

const operationTypeSlice = createSlice({
  name: 'operationType',
  initialState,
  reducers: {
    // Loading states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    
    // Error handling
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    
    // Data management
    setItems: (state, action: PayloadAction<OperationType[]>) => {
      state.items = action.payload;
    },
    setSelectedItem: (state, action: PayloadAction<OperationType | null>) => {
      state.selectedItem = action.payload;
    },
    addItem: (state, action: PayloadAction<OperationType>) => {
      state.items.unshift(action.payload);
    },
    updateItem: (state, action: PayloadAction<OperationType>) => {
      const index = state.items.findIndex(item => item.Id === action.payload.Id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
      if (state.selectedItem?.Id === action.payload.Id) {
        state.selectedItem = action.payload;
      }
    },
    removeItem: (state, action: PayloadAction<number>) => {
      state.items = state.items.filter(item => item.Id !== action.payload);
      if (state.selectedItem?.Id === action.payload) {
        state.selectedItem = null;
      }
    },
    
    // Form management
    setFormData: (state, action: PayloadAction<Partial<OperationTypeState['formData']>>) => {
      state.formData = { ...state.formData, ...action.payload };
    },
    resetFormData: (state) => {
      state.formData = initialState.formData;
    },
    
    // Validation
    setValidationErrors: (state, action: PayloadAction<string[]>) => {
      state.validationErrors = action.payload;
    },
    clearValidationErrors: (state) => {
      state.validationErrors = [];
    },
    
    // Search and filter
    setSearchParams: (state, action: PayloadAction<Partial<OperationTypeSearchParams>>) => {
      state.searchParams = { ...state.searchParams, ...action.payload };
    },
    resetSearchParams: (state) => {
      state.searchParams = initialState.searchParams;
    },
    
    // Pagination
    setPagination: (state, action: PayloadAction<Partial<OperationTypeState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    
    // Statistics
    setStatistics: (state, action: PayloadAction<OperationTypeState['statistics']>) => {
      state.statistics = action.payload;
    },
    
    // Bulk operations
    setSelectedIds: (state, action: PayloadAction<number[]>) => {
      state.selectedIds = action.payload;
    },
    addSelectedId: (state, action: PayloadAction<number>) => {
      if (!state.selectedIds.includes(action.payload)) {
        state.selectedIds.push(action.payload);
      }
    },
    removeSelectedId: (state, action: PayloadAction<number>) => {
      state.selectedIds = state.selectedIds.filter(id => id !== action.payload);
    },
    clearSelectedIds: (state) => {
      state.selectedIds = [];
    },
    setBulkOperationLoading: (state, action: PayloadAction<boolean>) => {
      state.bulkOperationLoading = action.payload;
    },
    
    // Success messages
    setSuccessMessage: (state, action: PayloadAction<string>) => {
      state.successMessage = action.payload;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = '';
    },
    
    // Reset state
    resetOperationTypeState: () => initialState,
  },
});

// Action creators
export const {
  setLoading,
  setError,
  setItems,
  setSelectedItem,
  addItem,
  updateItem,
  removeItem,
  setFormData,
  resetFormData,
  setValidationErrors,
  clearValidationErrors,
  setSearchParams,
  resetSearchParams,
  setPagination,
  setStatistics,
  setSelectedIds,
  addSelectedId,
  removeSelectedId,
  clearSelectedIds,
  setBulkOperationLoading,
  setSuccessMessage,
  clearSuccessMessage,
  resetOperationTypeState,
} = operationTypeSlice.actions;

// Saga action types
export const FETCH_OPERATION_TYPES = 'operationType/fetchOperationTypes';
export const FETCH_OPERATION_TYPE_BY_ID = 'operationType/fetchOperationTypeById';
export const CREATE_OPERATION_TYPE = 'operationType/createOperationType';
export const UPDATE_OPERATION_TYPE = 'operationType/updateOperationType';
export const DELETE_OPERATION_TYPE = 'operationType/deleteOperationType';
export const SEARCH_OPERATION_TYPES = 'operationType/searchOperationTypes';
export const FETCH_STATISTICS = 'operationType/fetchStatistics';
export const BULK_ACTIVATE = 'operationType/bulkActivate';
export const BULK_DEACTIVATE = 'operationType/bulkDeactivate';
export const BULK_DELETE = 'operationType/bulkDelete';
export const EXPORT_TO_CSV = 'operationType/exportToCSV';

// Saga action creators
export const fetchOperationTypes = (params?: OperationTypeSearchParams) => ({ type: FETCH_OPERATION_TYPES, payload: params });
export const fetchOperationTypeById = (id: number) => ({ type: FETCH_OPERATION_TYPE_BY_ID, payload: id });
export const createOperationType = (operationType: Omit<OperationType, 'Id'>) => ({ type: CREATE_OPERATION_TYPE, payload: operationType });
export const updateOperationType = (id: number, operationType: Partial<OperationType>) => ({ type: UPDATE_OPERATION_TYPE, payload: { id, operationType } });
export const deleteOperationType = (id: number) => ({ type: DELETE_OPERATION_TYPE, payload: id });
export const searchOperationTypes = (params: OperationTypeSearchParams) => ({ type: SEARCH_OPERATION_TYPES, payload: params });
export const fetchStatistics = () => ({ type: FETCH_STATISTICS });
export const bulkActivate = (ids: number[]) => ({ type: BULK_ACTIVATE, payload: ids });
export const bulkDeactivate = (ids: number[]) => ({ type: BULK_DEACTIVATE, payload: ids });
export const bulkDelete = (ids: number[]) => ({ type: BULK_DELETE, payload: ids });
export const exportToCSV = (params?: OperationTypeSearchParams) => ({ type: EXPORT_TO_CSV, payload: params });

export default operationTypeSlice.reducer; 